import type { DatevExport } from "@/apis/gql/types";
import {
  DownloadIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  RotateCcwIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { downloadExportFile } from "@/apis/exports/download";
import { AutoHeight } from "@/components/shared/page-layout/auto-height";
import { Badge } from "@/components/ui/badge";
import { AsyncButton, Button } from "@/components/ui/button";
import { Card, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ErrorMessage } from "@/components/ui/error";
import { LoadingSpinner } from "@/components/ui/spinner";
import { formatTimestampToString } from "@/lib/dates/dateUtils";
import { cn } from "@/lib/utils";
import { useGetAllDatevExports } from "@/queries/exports/get-all";

export const BankBookingExportHistory = ({
  onOpenExport,
  selectedExportId,
  onRetryExport,
}: {
  onOpenExport: (exportId: string) => void;
  selectedExportId?: string;
  onRetryExport: (months: Record<string, string[]>) => Promise<void>;
}) => {
  const { t } = useTranslation();

  const { data, loading, error, refetch } = useGetAllDatevExports({
    page: 1,
    limit: 50,
    entityType: "bank-transaction",
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="absolute flex justify-center items-center h-full">
        <ErrorMessage message={error.message} />
      </div>
    );
  }

  const getStateInfo = (state: string) => {
    const stateMap = {
      exported: {
        label: t("exports.states.exported"),
        dotColor: "bg-green-500",
        className: "bg-green-50 text-green-700 border-green-200",
      },
      failed: {
        label: t("exports.states.failed"),
        dotColor: "bg-red-500",
        className: "bg-red-50 text-red-700 border-red-200",
      },
      pending: {
        label: t("exports.states.pending"),
        dotColor: "bg-yellow-500",
        className: "bg-yellow-50 text-yellow-700 border-yellow-200",
      },
    };

    return (
      stateMap[state as keyof typeof stateMap] || {
        label: state,
        dotColor: "bg-gray-400",
        className: "bg-gray-50 text-gray-600 border-gray-200",
      }
    );
  };

  const handleOpenExport = (exportId: string) => {
    onOpenExport(exportId);
  };

  const handleRetryExport = async (exportItem: DatevExport) => {
    const stackName = exportItem.stackName;

    if (!stackName) {
      return;
    }

    const months = {
      [stackName]: exportItem.entityExports.map((entity) => entity.entityId),
    };

    await onRetryExport(months);

    refetch();
  };

  const handleDownloadExport = async (exportId: string, exportType: string) => {
    try {
      await downloadExportFile(exportId, exportType);
    } catch (error) {
      toast.error(t("generic_error"), { richColors: true });
    }
  };

  return (
    <AutoHeight className="flex-1">
      {({ height }) => (
        <div style={{ height }} className="overflow-y-auto">
          {data.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>{t("no_results")}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 py-4">
              {data.map((exportItem) => {
                const isNotBooked = exportItem.stackName === "not_booked";
                const stateInfo = getStateInfo(exportItem.state);

                const getExportLabel = () => {
                  if (isNotBooked) {
                    return `${t("export_from")} ${formatTimestampToString(
                      exportItem.exportedAt,
                    )} • ${t("todos.sort")}`;
                  }
                  return `${t("export_from")} ${formatTimestampToString(
                    exportItem.exportedAt,
                  )}`;
                };

                const isSelected =
                  selectedExportId === exportItem.datevExportId;

                return (
                  <Card
                    key={exportItem.datevExportId}
                    className={cn(
                      "transition-all duration-200 cursor-pointer shadow-none",
                      isSelected
                        ? "border-primary bg-primary/[0.08] shadow"
                        : "border-gray-200",
                    )}
                  >
                    <CardHeader className="p-3">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex justify-between items-center">
                          <CardTitle className="font-semibold text-sm">
                            {getExportLabel()}
                          </CardTitle>
                          <div className="flex items-center gap-1">
                            <Badge
                              variant="outline"
                              className={cn("gap-1", stateInfo.className)}
                            >
                              {stateInfo.label}
                            </Badge>
                            <span className="px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
                              {exportItem.entityCounts.total}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {exportItem.stackName && (
                            <span className="font-mono">
                              {t("stack")} {exportItem.stackName}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardFooter className="border-t p-3">
                      <div className="flex flex-row gap-2 w-full">
                        <div className="flex-1">
                          <Button
                            size="xs"
                            variant="outline"
                            className="gap-1 capitalize w-full"
                            onClick={() =>
                              handleOpenExport(exportItem.datevExportId)
                            }
                          >
                            <EyeIcon className="w-4 h-4" />
                            {t("open")}
                          </Button>
                        </div>
                        <div className="flex-1">
                          <AsyncButton
                            size="xs"
                            variant="outline"
                            className="gap-1 capitalize w-full"
                            onClick={() => handleRetryExport(exportItem)}
                          >
                            <RotateCcwIcon className="w-4 h-4" />
                            {t("retry")}
                          </AsyncButton>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="xs"
                              variant="outline"
                              className="px-2"
                            >
                              <EllipsisVerticalIcon className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <AsyncButton
                                size="xs"
                                variant="ghost"
                                className="gap-2 w-full justify-start cursor-pointer"
                                onClick={() =>
                                  handleDownloadExport(
                                    exportItem.datevExportId,
                                    exportItem.exportType,
                                  )
                                }
                              >
                                <DownloadIcon className="w-4 h-4" />
                                {t("download_datev_format")}
                              </AsyncButton>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </AutoHeight>
  );
};
